/* eslint-disable import/prefer-default-export */
import { defineStore, storeToRefs } from 'pinia';
import axios from 'axios';
import { useCompanyStore } from '@/services/UserService/useCompanyStore';
import { useEntriesStore } from '@/services/useEntriesStore';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import { useMeStore } from '@/services/UserService/useMeStore';
import { extractDate, extractTime } from '@/utils/dateTimeUtils';
export const useEntryStore = defineStore('entryStore', {
  state: () => {
    const {
      companySettings,
      loadMyCompany
    } = useCompanyStore();
    const entriesStore = useEntriesStore();
    const {
      handleError
    } = useSnackbarHandler();
    loadMyCompany();
    return {
      companySettings,
      entriesStore,
      handleError,
      entryId: undefined,
      history: [],
      saving: false
    };
  },
  actions: {
    async ensureShortDescriptionsLoaded() {
      const {
        features
      } = storeToRefs(useMeStore());
      if (features.value?.stripDescriptions) {
        await this.entriesStore.includeShortDescriptions();
      }
    },
    async createEntry(body) {
      body = this.formatEntry(body);
      this.saving = true;
      try {
        const response = await axios({
          url: '/entries',
          data: body,
          method: 'POST'
        });
        await this.ensureShortDescriptionsLoaded();
        const formattedEntry = this.entriesStore.formatEntryForDisplay(response.data);
        const entryWithFlag = {
          ...formattedEntry,
          isNewEntry: true
        };
        this.entriesStore.entries.push(entryWithFlag);
        this.entriesStore.recalculateSummary();
        this.saving = false;
        return true;
      } catch (error) {
        this.saving = false;
        this.handleError(error || 'Error creating entry');
        return false;
      }
    },
    async updateEntry(update) {
      update = this.formatEntry(update);
      this.saving = true;
      try {
        const response = await axios({
          url: `/entries/${update.id}`,
          method: 'PUT',
          data: update
        });
        await this.ensureShortDescriptionsLoaded();
        const formattedEntry = this.entriesStore.formatEntryForDisplay(response.data);
        const idx = this.entriesStore.entries.findIndex(e => e.id === update.id);
        if (idx !== -1) this.entriesStore.entries[idx] = formattedEntry;
        this.entriesStore.recalculateSummary();
        this.saving = false;
        return formattedEntry;
      } catch (error) {
        this.handleError(error || 'Error updating entry');
        this.saving = false;
      }
    },
    async getEntry(id) {
      try {
        const response = await axios({
          url: `/entries/${id}`,
          method: 'GET'
        });
        return response.data;
      } catch (error) {
        this.handleError(error || 'Error fetching entry');
      }
    },
    async deleteEntry(id) {
      this.saving = true;
      try {
        const response = await axios({
          url: `/entries/${id}`,
          method: 'DELETE'
        });
        this.entriesStore.entries = this.entriesStore.entries.filter(e => e.id !== id);
        this.entriesStore.recalculateSummary();
        this.saving = false;
        return response.data;
      } catch (error) {
        this.handleError(error || 'Error deleting entry');
        this.saving = false;
      }
    },
    async markAsBilled(id, billed, shouldRemove = false) {
      this.saving = true;
      try {
        const response = await axios({
          url: `/entries/${id}/billed`,
          method: 'PUT',
          data: {
            billed
          }
        });
        await this.ensureShortDescriptionsLoaded();
        const formattedEntry = this.entriesStore.formatEntryForDisplay(response.data);
        if (shouldRemove) {
          // Remove entry from current view
          this.entriesStore.entries = this.entriesStore.entries.filter(e => e.id !== id);
        } else {
          // Update entry in place
          const idx = this.entriesStore.entries.findIndex(e => e.id === id);
          if (idx !== -1) {
            this.entriesStore.entries[idx] = formattedEntry;
          }
        }
        this.entriesStore.recalculateSummary();
        this.saving = false;
        return formattedEntry;
      } catch (error) {
        this.handleError(error || `Error ${billed ? 'marking' : 'unmarking'} entry as billed`);
        this.saving = false;
        throw error;
      }
    },
    formatEntry(entry) {
      const formattedEntry = {
        ...entry
      };
      formattedEntry.matter = {
        id: formattedEntry.matter?.id
      };
      formattedEntry.account = {
        id: formattedEntry.account?.id
      };

      // TODO (Story #25310): Remove legacy startTime/endTime conversion after migration complete
      // Currently sending both for backward compatibility
      formattedEntry.endTime = this.convertToDate(formattedEntry.date, formattedEntry.endTime);
      formattedEntry.startTime = this.convertToDate(formattedEntry.date, formattedEntry.startTime);

      // NEW: Send localDateTime format for backend (Story #25310)
      // Backend will handle timezone conversion using user's timezone setting
      if (formattedEntry.date && entry.startTime) {
        formattedEntry.localDateTime = {
          date: extractDate(formattedEntry.date),
          time: extractTime(entry.startTime)
        };
      }
      formattedEntry.billingType = typeof entry.billingType != 'string' ? formattedEntry.billingType?.id : formattedEntry.billingType;
      formattedEntry.chargeId = formattedEntry.charge?.id;
      formattedEntry.charge = undefined;
      formattedEntry.client = undefined;

      // Map rate.edited from frontend to rateEdited for backend
      // Always send explicit true/false (never undefined) so backend knows current state
      formattedEntry.rateEdited = formattedEntry.rate?.edited === true;
      return formattedEntry;
    },
    convertToDate(entryDate, time) {
      if (!time) return;
      let date = new Date();
      if (entryDate) {
        date = new Date(entryDate);
      }
      date.setHours(parseInt(time.slice(0, 2)));
      date.setMinutes(parseInt(time.slice(3, 5)));
      return date.toISOString();
    },
    async loadEntryHistory(entryId) {
      if (!entryId) return;
      if (this.entryId === entryId) {
        return this.history;
      }
      try {
        const response = await axios({
          url: `/audit/entries/${entryId}`,
          method: 'GET'
        });
        this.$patch({
          entryId,
          history: response.data
        });
        return response.data;
      } catch (error) {
        this.handleError(error || 'Error loading entry history');
      }
    }
  }
});